import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";

import { USER_ROLES } from "../../utils/constants";
import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import useUpdateVisitAssessmentAreasMutation from "../../hooks/useUpdateVisitAssessmentAreasMutation";
import useGetVisitAssessmentAreas from "../../hooks/useGetVisitAssessmentAreas";

const AIAssessmentModule = ({
  trial,
  role,
  selectedSessionId,
  selectedVisit,
  patientId,
  visitId,
  step,
  assessmentAreas = [],
  disabled = false,
  headerless = false,
  manageTestId,
  isTrialLocked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);

  // Local dialog state — tracks pending changes before Save is clicked
  const [localSelectedAreas, setLocalSelectedAreas] = useState([]);
  const [localIsAllAiAreas, setLocalIsAllAiAreas] = useState(false);
  const [localDisallowInv, setLocalDisallowInv] = useState(false);
  const [localApplyToFollowing, setLocalApplyToFollowing] = useState(false);

  const stepId = step?.id;
  const label = step?.title || "AI Assessment";

  const updateMutation = useUpdateVisitAssessmentAreasMutation();

  // Step-scoped truth from the backend — always fetched (not just in patient mode) so a
  // repeated module's CRO view reads this step's own items instead of the flat, union-of-
  // all-steps `selectedVisit.assessment_areas` field.
  const { data: visitAreasData } = useGetVisitAssessmentAreas({
    visitId,
    patientId,
    sessionId: selectedSessionId,
    stepId,
  });

  const isInvestigator =
    role === USER_ROLES.INVESTIGATOR ||
    role === USER_ROLES.SUB_INVESTIGATOR ||
    role === USER_ROLES.CRC;

  const isReadOnly =
    isTrialLocked ||
    role === USER_ROLES.REVIEWER ||
    (patientId && selectedVisit.visit_number === -1) ||
    (patientId && selectedVisit?.disallow_inv_assessment_areas_selection);

  // Adverse Event visits must always keep the investigator locked out of picking
  // assessment areas — CRO can't turn this off.
  const isAdverseEventVisit = selectedVisit?.visit_number === -1;

  // Sync enabled state from server data. In patient mode, only an explicit session
  // selection counts as "on" — items merely inherited from the visit are not a real choice yet.
  // Exception: when the investigator is locked out of picking (disallow_inv), there will
  // never be an explicit session selection, so the visit's forced items count as "on" instead.
  useEffect(() => {
    if (!patientId) {
      if (visitAreasData?.assessment_areas?.length) {
        setEnabled(true);
      }
    } else if (
      (visitAreasData?.is_session_selection ||
        visitAreasData?.disallow_inv_assessment_areas_selection) &&
      visitAreasData?.assessment_areas?.length
    ) {
      setEnabled(true);
    }
  }, [patientId, visitAreasData]);

  // Initialise local dialog flags from this step's visit-level truth each time the dialog opens
  useEffect(() => {
    if (open) {
      setLocalIsAllAiAreas(!!visitAreasData?.is_all_ai_areas);
      setLocalDisallowInv(
        isAdverseEventVisit || !!visitAreasData?.disallow_inv_assessment_areas_selection
      );
      setLocalApplyToFollowing(!!selectedVisit?.apply_areas_to_following);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate the investigator's selection — only when the session has an explicit
  // override; otherwise the visit's items are just inherited, not actually chosen yet
  useEffect(() => {
    if (open) {
      if (!patientId) {
        setLocalSelectedAreas(visitAreasData?.assessment_areas || []);
      } else {
        setLocalSelectedAreas(
          visitAreasData?.is_session_selection ||
            visitAreasData?.disallow_inv_assessment_areas_selection
            ? visitAreasData?.assessment_areas || []
            : []
        );
      }
    }
  }, [open, patientId, visitAreasData]); // eslint-disable-line react-hooks/exhaustive-deps

  const localSelectedIds = useMemo(() => localSelectedAreas.map((a) => a.id), [localSelectedAreas]);

  const dynamicPhotoTypes = useMemo(() => {
    const photoTypeMap = new Map();
    for (const selected of localSelectedAreas) {
      const fullArea = assessmentAreas.find((a) => a.id === selected.id) || selected;
      for (const photoType of fullArea.required_photo_types || []) {
        if (!photoTypeMap.has(photoType.id)) photoTypeMap.set(photoType.id, photoType);
      }
    }
    return Array.from(photoTypeMap.values());
  }, [localSelectedAreas, assessmentAreas]);

  const handleToggleEnabled = (e) => {
    const checked = e.target.checked;
    setEnabled(checked);

    if (!checked) {
      updateMutation.mutate({
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        assessmentAreas: [],
        isAllAiAreas: false,
        disallowInvAssessmentAreasSelection: false,
        applyAreasToFollowing: false,
      });
    }
  };

  const toggleArea = (area) => {
    if (isReadOnly) return;

    setLocalSelectedAreas((prev) => {
      const exists = prev.some((a) => a.id === area.id);
      return exists ? prev.filter((a) => a.id !== area.id) : [...prev, area];
    });
  };

  const handleSaveForAllVisits = () => {
    setLocalIsAllAiAreas((prev) => !prev);
  };

  const handleDisableInvAiSelection = () => {
    setLocalDisallowInv((prev) => !prev);
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        assessmentAreas: localSelectedAreas.map((a) => ({ label: a.label || a.name })),
        isAllAiAreas: localIsAllAiAreas,
        disallowInvAssessmentAreasSelection: headerless ? false : localDisallowInv,
        applyAreasToFollowing: localApplyToFollowing,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  if (!trial?.has_ai_evaluation) return null;

  // Investigators are not shown modules the CRO did not configure for this visit
  if (
    isInvestigator &&
    patientId &&
    !headerless &&
    selectedVisit?.visit_number >= 0 &&
    visitAreasData?.visit_configured === false
  ) {
    return null;
  }

  return (
    <Box sx={headerless ? {} : { width: "100%" }}>
      {headerless ? (
        <CustomButton
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          data-testid={manageTestId}
        >
          {isReadOnly ? "View" : "Manage"}
        </CustomButton>
      ) : (
        /* Header */
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={enabled}
              onChange={handleToggleEnabled}
              disabled={isReadOnly || disabled || updateMutation.isPending}
            />

            <Typography variant="subtitle1" fontWeight={600}>
              {label}
            </Typography>
          </Stack>

          {enabled && (
            <Stack direction="row" spacing={1} alignItems="center">
              {patientId && (
                <Tooltip title="Go to Photos">
                  <IconButton
                    component={Link}
                    target="_blank"
                    to={`/patient/${patientId}/photos/${!!selectedSessionId ? selectedSessionId : selectedVisit.photo_session_id}/?visitName=${encodeURIComponent(selectedVisit.visit_name)}`}
                    size="small"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CustomButton size="small" variant="outlined" onClick={() => setOpen(true)}>
                {isReadOnly ? "View" : "Manage"}
              </CustomButton>
            </Stack>
          )}
        </Stack>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isReadOnly ? "View" : "Manage"} {label}
        </DialogTitle>

        <DialogContent dividers>
          {/* CRO flags */}
          {!isReadOnly &&
            (role === USER_ROLES.CRO ||
              role === USER_ROLES.CRO_COORDINATOR ||
              role === USER_ROLES.DATA_MANAGER ||
              role === USER_ROLES.SUPER_ADMIN) && (
              <Box sx={{ mb: 1 }}>
                {!headerless && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={localDisallowInv}
                      onChange={handleDisableInvAiSelection}
                      disabled={isAdverseEventVisit}
                    />
                    <Typography variant="body2">
                      Disable AI Assessment Areas selection for investigator
                      {isAdverseEventVisit && " (always enabled for Adverse Event visits)"}
                    </Typography>
                  </Stack>
                )}

                {!patientId && canSaveForFollowingVisits(selectedVisit) && (
                  <SaveForFollowingVisitsCheckbox
                    label="Save AI Assessment Areas configurations for all coming visits"
                    checked={localIsAllAiAreas}
                    onChange={handleSaveForAllVisits}
                  />
                )}
              </Box>
            )}

          <Stack direction="row" spacing={3}>
            {/* AI Assessment Areas */}
            <Box sx={{ width: "50%" }}>
              <Typography variant="subtitle2" mb={1}>
                AI Assessment Areas
              </Typography>

              <List>
                {assessmentAreas.map((area) => {
                  const checked = localSelectedIds.includes(area.id);

                  return (
                    <ListItem key={area.id} disableGutters sx={{ mb: 1 }}>
                      <Paper
                        sx={{
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={isReadOnly}
                          onChange={() => toggleArea(area)}
                        />
                        <ListItemText primary={area.label} />
                      </Paper>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            {/* AI Photos (read-only) */}
            <Box sx={{ width: "50%" }}>
              <Typography variant="subtitle2" mb={1}>
                AI Photos
              </Typography>

              <List>
                {dynamicPhotoTypes.map((photo) => (
                  <ListItem key={photo.id} disableGutters sx={{ mb: 1 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Checkbox checked disabled />
                      <ListItemText primary={photo.label} />
                    </Paper>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>

          {/* Apply to following visits - Investigator/CRC */}
          {!isReadOnly && isInvestigator && patientId && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <Checkbox
                checked={localApplyToFollowing}
                onChange={() => setLocalApplyToFollowing((prev) => !prev)}
              />
              <Typography variant="body2">Apply to all following visits</Typography>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAssessmentModule;
